/**
 * F5.3 Wave-4 — Postgres data-type vocabulary and position detection.
 *
 * Surfaces the curated PG type list at "column-type" / "parameter-type" /
 * "CAST target" / "::" cast-suffix slots. The grammar's clause classifier
 * returns `'unknown'` at these positions (none of the standard
 * CompletionPositionKind arms fits — they're not WHERE / SELECT-list /
 * group-by / etc.), so the resolver invokes `isPgsqlDataTypePosition` as a
 * narrow text-walk fallback to recognise:
 *
 *   1. `CREATE TABLE foo (colName |` — column-list inside CREATE TABLE.
 *   2. `ALTER TABLE foo ADD COLUMN colName |` — column add.
 *   3. `CREATE FUNCTION foo(param_name |` — routine parameter declaration.
 *   4. `CREATE FUNCTION foo(...) RETURNS |` — function return type.
 *   5. `CAST(expr AS |)` — CAST target type.
 *   6. `expr::|` — PG's `::` cast suffix.
 *   7. `DECLARE  v_total BIGINT;  v_threshold |` — a plpgsql DECLARE-section
 *      variable's type (pgsql script.33 §3).
 *   8. `CREATE TYPE t AS ( street VARCHAR(120), city | )` — a COMPOSITE type's
 *      attribute type (pgsql script.36 §3/§4).
 *
 * Shapes 7 and 8 are why this text-walk still exists alongside the token slot
 * seam, which cannot reach either:
 *   - shape 7's statement isolation cuts at the `;` that ends the PREVIOUS
 *     declaration, so the slot's token slice is the bare identifier
 *     `v_threshold` with no DECLARE in sight. Only a scan that reads BEHIND
 *     the statement start can tell a DECLARE-section line from a fresh
 *     statement, and that is exactly what a resolver-side walk over the whole
 *     buffer can do.
 *   - shape 8's caret sits inside a `DO $$ … $$` block whose clause is
 *     `subquery`, a NAMED clause. `data-type` is not a DDL-family kind, so it
 *     cannot win the DDL-override gate and the unknown-gate does not apply —
 *     but this walk runs independently of `positionKind`, so the answer lands
 *     anyway.
 *
 * TYPED-PREFIX PARITY: every shape is recognised with the caret ATTACHED to a
 * partial type name (`RETURNS tri|`, `city VAR|`) — the partial is the answer
 * being typed, not context, so it is dropped before the shape match.
 *
 * Pure text-walk — no tokenizer dependencies beyond the shared tokenizer.
 */

import { findEnclosingDollarQuotedBody } from '@/features/sql-grammar/document/editorContextScan';

import { dropAttachedPartialTokens, tokenize, type Token } from '../context/tokenizer';
import { isConstraintLeadWord } from './constraintLeadWords';

const ENGINE = 'pgsql' as const;

/**
 * Curated PG data type vocabulary surfaced at column-type / CAST-target
 * positions. Aligns with the types named in the autocomplete coverage
 * scripts (29, 30, 33, …) and covers the common-case categories.
 */
const PGSQL_DATA_TYPES: readonly string[] = [
  'SMALLINT',
  'INTEGER',
  'BIGINT',
  'DECIMAL',
  'NUMERIC',
  'REAL',
  'DOUBLE PRECISION',
  'SERIAL',
  'BIGSERIAL',
  'SMALLSERIAL',
  'MONEY',
  'CHAR',
  'VARCHAR',
  'TEXT',
  'BYTEA',
  'BOOLEAN',
  'DATE',
  'TIME',
  'TIMETZ',
  'TIMESTAMP',
  'TIMESTAMPTZ',
  'INTERVAL',
  'JSON',
  'JSONB',
  'UUID',
  'INET',
  'CIDR',
  'MACADDR',
  'XML',
  'POINT',
  'LINE',
  'LSEG',
  'BOX',
  'PATH',
  'POLYGON',
  'CIRCLE',
];

export function getPgsqlDataTypes(): readonly string[] {
  return PGSQL_DATA_TYPES;
}

/**
 * The two PSEUDO-types only a function's RETURNS clause accepts. PG rejects
 * them everywhere a real type is required (`CREATE TABLE t (c trigger)` is an
 * error), so they are NOT in `PGSQL_DATA_TYPES` — the resolver appends them
 * only when `isPgsqlReturnsTypeSlot` says the caret is in a RETURNS clause.
 * Lower-case because that is how PG spells them in `pg_proc.prorettype`.
 * Closes pgsql script.35 §1.
 */
export const PGSQL_RETURNS_ONLY_TYPES: readonly string[] = ['trigger', 'event_trigger'];

/**
 * True when the cursor sits in a routine's `RETURNS ▮` clause (shape 4 above),
 * with or without a partial type already typed. Split out of
 * `isPgsqlDataTypePosition` so the resolver can widen the vocabulary at this
 * one slot without widening it everywhere.
 */
export function isPgsqlReturnsTypeSlot(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return false;
  const tokens = dropAttachedPartialTokens(raw, cursor);
  if (identUpper(tokens[tokens.length - 1]) !== 'RETURNS') return false;
  return isInsideCreateFunctionHeader(tokens);
}

/**
 * Detect whether the cursor sits at a PG "data-type" slot. Returns true for
 * the six shapes documented in the module header. Conservative — when in
 * doubt the function returns false so regular emission still fires.
 */
export function isPgsqlDataTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return false;
  const rawPrev = raw[raw.length - 1]!;

  // Shape 6: `::|` PG cast suffix — previous token is `::` (`dcolon`).
  // The tokenizer may emit this as a single `dcolon` token or as two `:`
  // ops. Accept either form. Read from the RAW tail: the `::` itself is the
  // introducer, never a partial answer.
  if (rawPrev.type === 'op' && (rawPrev.text === '::' || rawPrev.text === ':')) {
    if (rawPrev.text === '::') return true;
    // Two-colon form: prev is `:`, before is `:`.
    const before = raw[raw.length - 2];
    if (before && before.type === 'op' && before.text === ':') return true;
  }

  // A bare word the caret is ATTACHED to is the type being typed, not part of
  // the slot's context — drop it so `RETURNS tri|` reads as `RETURNS |`.
  const tokens = dropAttachedPartialTokens(raw, cursor);
  const attachedPartial = tokens.length !== raw.length;
  const prev = tokens[tokens.length - 1];
  if (!prev) return false;

  // Shape 6 with a partial already typed (`payment_date::dat|`): the `::`
  // introducer sits BEFORE the dropped word, so it is only visible on the
  // effective tail. Without this the cast-target type list vanished on the
  // first keystroke (pgsql coverage script.48 §2).
  if (attachedPartial && prev.type === 'op' && (prev.text === '::' || prev.text === ':')) {
    if (prev.text === '::') return true;
    const before = tokens[tokens.length - 2];
    if (before && before.type === 'op' && before.text === ':') return true;
  }

  // Shape 5: ... AS |  (CAST(expr AS |))
  if (identUpper(prev) === 'AS') {
    if (isInsideCallTo(tokens, ['CAST'])) return true;
  }

  // Shape 4: `... RETURNS |` — previous token is RETURNS at the head of a
  // CREATE FUNCTION statement.
  if (identUpper(prev) === 'RETURNS') {
    if (isInsideCreateFunctionHeader(tokens)) return true;
  }

  // Shape 1 / 2 / 3 / 8 — previous token is an identifier (the column /
  // attribute / parameter name) followed by a data-type slot.
  if (prev.type === 'ident' && !isConstraintLeadWord(prev.text)) {
    if (isInsideCreateTableColumnList(tokens)) return true;
    if (isInsideCreateTypeAttributeList(tokens)) return true;
    if (followsAlterTableAddColumn(tokens)) return true;
    if (isInsideCreateFunctionParamList(tokens)) return true;
    // Shape 7 — a plpgsql DECLARE-section variable. Needs the buffer BEFORE
    // the isolated statement, so it is checked last and gets the full source.
    // `typeSlotStart` is where the TYPE begins: the dropped partial's own
    // start when the caret is attached to one, the caret otherwise.
    const typeSlotStart = attachedPartial ? rawPrev.start : cursor;
    if (isPlpgsqlDeclareTypeSlot(source, statementStart, tokens, typeSlotStart)) return true;
  }

  return false;
}

/**
 * True when the caret sits after a SCHEMA QUALIFIER inside a type slot —
 * `price public.▮` / `price public.aca▮` — and returns the qualifier.
 *
 * PG lets a column's type be schema-qualified (`public.acact_email` names a
 * DOMAIN), so `public.▮` in a column definition is a type-namespace drill, not
 * a relation drill. `deriveCompletionContext` short-circuits to
 * `'dotted-access'` before the slot seam runs whenever a dotted base is
 * present, so the resolver's dotted branch is where this has to be recognised:
 * it strips the `<qualifier> .` tail and re-tests the remaining prefix with
 * `isPgsqlDataTypePosition`. Closes pgsql script.39 §5/§6 + script.40 §4/§5.
 *
 * Returns the qualifier verbatim (unquoted, case preserved) or `null`.
 */
export function findPgsqlQualifiedTypeSlotSchema(
  source: string,
  cursor: number,
  statementStart: number,
): string | null {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return null;
  let i = raw.length - 1;
  // An attached partial type name after the dot (`public.aca▮`).
  const last = raw[i]!;
  if (last.end === cursor && (last.type === 'ident' || last.type === 'kw')) i -= 1;
  if (i < 0 || raw[i]!.type !== 'dot') return null;
  i -= 1;
  const qualifier = raw[i];
  if (!qualifier || qualifier.type !== 'ident') return null;
  // The prefix WITHOUT the `<qualifier> .` tail must itself be a type slot.
  // Re-tokenising from the qualifier's start is what makes the caret look like
  // the bare `price ▮` position the shapes above recognise.
  if (!isPgsqlDataTypePosition(source, qualifier.start, statementStart)) return null;
  return unquote(qualifier.text);
}

/** Strip one layer of double quoting (PG's only identifier quote). */
function unquote(raw: string): string {
  return raw.startsWith('"') ? raw.slice(1, -1).replace(/""/g, '"') : raw;
}

/**
 * True when the cursor sits at the TYPE of a plpgsql DECLARE-section variable:
 *
 *     AS $$
 *     DECLARE
 *       v_total_rentals BIGINT;
 *       v_threshold     ▮
 *     BEGIN
 *
 * Statement isolation cuts at the `;` that closes the previous declaration, so
 * the isolated slice is just `v_threshold` — the DECLARE keyword lives BEHIND
 * `statementStart`. This is the one shape in the file that reads the buffer
 * before that boundary: it scans backwards for the nearest `DECLARE` /
 * `BEGIN` word and fires only when DECLARE is the closer of the two (inside
 * the body, `BEGIN` means the declaration section is already over and a bare
 * identifier is a statement, not a variable name).
 *
 * The FIRST declaration in the section is the other half of the shape:
 *
 *     DO $$
 *     DECLARE
 *       v_customer_id ▮
 *
 * There is no previous `;` to cut at, so the slice is `DECLARE v_customer_id`
 * — two tokens with the keyword in front. Same slot, different slice; both are
 * accepted. Closes pgsql coverage script.35 §2 / script.37 §1 / script.43 §1,
 * which all declare exactly one variable and so only ever produce this form.
 *
 * Three guards keep it off everything else that can leave a single bare word
 * as the whole isolated statement:
 *   - the slice must be exactly ONE identifier — the variable name — or that
 *     identifier led by `DECLARE`, and in the second case the caret must sit
 *     inside a routine body, which is what tells a PL/pgSQL declaration from
 *     SQL's own `DECLARE <name> CURSOR FOR …` statement;
 *   - that word must not be a PL/pgSQL block keyword. `BEGIN` / `END` /
 *     `RETURN` and friends are not in `SQL_KEYWORDS`, so they lex as plain
 *     identifiers and `BEGIN ▮` would otherwise read as "a variable named
 *     BEGIN, awaiting its type";
 *   - the type must sit on the SAME LINE as the name. A declaration is written
 *     `v_threshold  INTEGER;` on one line, whereas the caret on the line AFTER
 *     a bare word is the start of a new statement.
 */
const PLPGSQL_BLOCK_WORDS: ReadonlySet<string> = new Set([
  'BEGIN', 'END', 'DECLARE', 'RETURN', 'IF', 'ELSE', 'ELSIF', 'LOOP', 'WHILE',
  'FOR', 'FOREACH', 'EXCEPTION', 'CASE', 'PERFORM', 'RAISE', 'CONTINUE', 'EXIT',
  'NULL', 'GET', 'ASSERT', 'CALL', 'COMMIT', 'ROLLBACK',
]);

function isPlpgsqlDeclareTypeSlot(
  source: string,
  statementStart: number,
  tokens: readonly Token[],
  typeSlotStart: number,
): boolean {
  // The FIRST declaration of the section has no previous `;` for statement
  // isolation to cut at, so `DECLARE` lands INSIDE the slice and the slice is
  // two tokens (`DECLARE v_event`) instead of one. Both shapes are the same
  // slot; only the second one can read DECLARE off the buffer behind it.
  //
  // `DECLARE` in the slice is not proof of a PL/pgSQL section on its own: SQL
  // has a top-level `DECLARE <name> CURSOR FOR …`, whose second token is also
  // a bare name awaiting a keyword. Requiring a routine body around the caret
  // separates the two — a cursor declaration is a statement of its own, never
  // inside `AS $$ … $$` / `DO $$ … $$`.
  const declareLeadsSlice = tokens.length === 2 && identUpper(tokens[0]) === 'DECLARE';
  if (declareLeadsSlice) {
    if (!findEnclosingDollarQuotedBody(source, typeSlotStart)) return false;
  } else if (tokens.length !== 1) {
    return false;
  }
  const name = tokens[tokens.length - 1]!;
  if (PLPGSQL_BLOCK_WORDS.has(name.text.toUpperCase())) return false;
  if (source.slice(name.end, typeSlotStart).includes('\n')) return false;
  if (declareLeadsSlice) return true;
  const before = source.slice(0, statementStart);
  const declareAt = lastWordIndex(before, 'DECLARE');
  if (declareAt < 0) return false;
  return lastWordIndex(before, 'BEGIN') < declareAt;
}

/** Index of the last whole-word occurrence of `word` (case-insensitive), or -1. */
function lastWordIndex(haystack: string, word: string): number {
  const re = new RegExp(`\\b${word}\\b`, 'gi');
  let last = -1;
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(haystack)) !== null) last = m.index;
  return last;
}

/**
 * True when the cursor sits inside the attribute list of a COMPOSITE type
 * declaration — `CREATE TYPE <name> AS ( street VARCHAR(120), city ▮ )`.
 *
 * The opener must follow `AS` DIRECTLY: `AS ENUM (` (a label list) and
 * `AS RANGE (` (named `subtype = …` options) are different slots, and the head
 * must be a `CREATE [OR REPLACE] TYPE <name>` so a CTE's or view's `AS (` can
 * never match. As with the CREATE TABLE column list, the attribute name has to
 * sit at a FRESH slot (right after the `(` or a `,`).
 */
function isInsideCreateTypeAttributeList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  if (identUpper(tokens[lparenIdx - 1]) !== 'AS') return false;
  // `CREATE [OR REPLACE] TYPE <dotted name> AS (`.
  let i = lparenIdx - 2;
  if (i >= 0 && tokens[i]!.type === 'ident') i -= 1;
  if (i >= 0 && tokens[i]!.type === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.type !== 'ident') return false;
    i -= 1;
  }
  if (identUpper(tokens[i]) !== 'TYPE') return false;
  i -= 1;
  if (identUpper(tokens[i]) === 'REPLACE') i -= 2; // … OR REPLACE
  if (identUpper(tokens[i]) !== 'CREATE') return false;
  // Fresh-attribute guard — the name's own predecessor is the `(` or a `,`.
  const beforePrev = tokens[tokens.length - 2];
  if (!beforePrev) return false;
  return beforePrev.type === 'lparen' || beforePrev.type === 'comma';
}

// ─────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

/** Index of the unclosed `(` enclosing the cursor, or -1. */
function findEnclosingLparenIndex(tokens: readonly Token[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'rparen') depth += 1;
    else if (t.type === 'lparen') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}

function isInsideCallTo(
  tokens: readonly Token[],
  callNames: readonly string[],
): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  const before = tokens[lparenIdx - 1];
  const upper = identUpper(before);
  if (!upper) return false;
  return callNames.includes(upper);
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a `CREATE TABLE`. Walk back from the opener looking for a TABLE keyword.
 */
function isInsideCreateTableColumnList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  let sawTable = false;
  for (let i = lparenIdx - 1; i >= Math.max(0, lparenIdx - 12); i -= 1) {
    const u = identUpper(tokens[i]);
    if (u === 'TABLE') {
      sawTable = true;
      break;
    }
    const t = tokens[i]!;
    if (t.type === 'lparen' || t.type === 'rparen' || t.type === 'comma') break;
  }
  if (!sawTable) return false;
  // Validate that the prev (column-name) sits at a "fresh column slot" — i.e.
  // its previous significant token is the column-list `(` or a `,` separator.
  // Otherwise the prev is mid-clause (e.g. `REFERENCES`, `CONSTRAINT`,
  // `FOREIGN KEY`) and the data-type emitter must NOT fire.
  const beforePrev = tokens[tokens.length - 2];
  if (!beforePrev) return false;
  if (beforePrev.type === 'lparen' || beforePrev.type === 'comma') return true;
  return false;
}

/**
 * True when the cursor sits at `ALTER TABLE x ADD COLUMN colName |` outside
 * any enclosing paren. Walk back from the cursor — the most recent
 * `ADD COLUMN` (case-insensitive) sequence must precede the cursor and no
 * statement terminator should intervene.
 */
function followsAlterTableAddColumn(tokens: readonly Token[]): boolean {
  let sawColumn = false;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = identUpper(t);
    if (t.type === 'semicolon') return false;
    if (u === 'COLUMN') {
      sawColumn = true;
      continue;
    }
    if (u === 'ADD' && sawColumn) {
      // Now walk forward of 'ADD' looking for an ALTER…TABLE before it.
      for (let j = i - 1; j >= 0; j -= 1) {
        const jt = tokens[j]!;
        const ju = identUpper(jt);
        if (jt.type === 'semicolon') return false;
        if (ju === 'TABLE') {
          for (let k = j - 1; k >= 0; k -= 1) {
            const ku = identUpper(tokens[k]);
            if (ku === 'ALTER') return true;
            if (tokens[k]!.type === 'semicolon') return false;
          }
        }
      }
      return false;
    }
  }
  return false;
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a `CREATE FUNCTION` / `CREATE PROCEDURE` header.
 */
function isInsideCreateFunctionParamList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  const head = tokens.slice(0, lparenIdx);
  const upperHead = head.map((t) => identUpper(t)).filter((u): u is string => !!u);
  let i = 0;
  if (upperHead[i] !== 'CREATE') return false;
  i += 1;
  if (upperHead[i] === 'OR') {
    i += 1;
    if (upperHead[i] !== 'REPLACE') return false;
    i += 1;
  }
  return upperHead[i] === 'FUNCTION' || upperHead[i] === 'PROCEDURE';
}

/**
 * True when the cursor sits at `CREATE FUNCTION foo(...) RETURNS |` — at
 * the head of a CREATE FUNCTION statement, no enclosing paren, RETURNS as
 * the most recent significant keyword.
 */
function isInsideCreateFunctionHeader(tokens: readonly Token[]): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  const upperAll = tokens
    .map((t) => identUpper(t))
    .filter((u): u is string => !!u);
  let i = 0;
  if (upperAll[i] !== 'CREATE') return false;
  i += 1;
  if (upperAll[i] === 'OR') {
    i += 1;
    if (upperAll[i] !== 'REPLACE') return false;
    i += 1;
  }
  return upperAll[i] === 'FUNCTION' || upperAll[i] === 'PROCEDURE';
}
